// >>> **THE LAST SURFACE A NEW MEMBER SEES BEFORE SHE LANDS, AND NOTHING COULD REACH IT.** <<<
//
// It moved as F4-1/F4-3's line payment, and it is R8's own currency: **the thing that had to move is
// the thing nothing could reach, and it is the thing this round is about.** Her F4-3 clause is
// *"she lands directly in an Ask Lala conversation ... never the room tour"*, and the landing this
// component's own `onEnter` fires — `enterRoom()` in App.tsx — is the one call site that decides it.
// There is exactly ONE mount and exactly ONE caller, measured, so out here the whole of that
// decision is readable by an importing gate rather than by a regex over ten thousand lines.
//
// **IT HOLDS TWO HOOKS AND CAME ANYWAY, ON R10-3's RULING AS R67 AND R81 RESTATED IT: a hook only
// blocks a component a gate CALLS.** The reach this buys is the IMPORT — `ModalBackdrop` is already
// out here (R81), so nothing cascaded and App.tsx pays one import line for eighty-two.
//
// **AND THE ORPHANED COMMENT BLOCK THAT SAT ABOVE IT IN App.tsx WAS DELETED RATHER THAN CARRIED**
// (R127's class): eleven lines documenting `ModalBackdrop`'s portal, its escape key and its
// deliberate no-background-tap ruling, left standing when R81 moved that component to its own file —
// where all three are written out again, in more detail, at their own declaration. A header naming a
// thing that lives somewhere else is documentation a reader takes for installation (R121/R122), and
// nothing was lost: `src/components/ModalBackdrop.tsx` carries every word of it.
//
// >>> **AND THE EXTRACTION FOUND A DEAD PROP, WHICH CAME OUT WITH IT (R216, IT #146's precedent).**
// <<< `canEnter` was declared, typed, passed at the one call site from a `roomCanEnter` state with
// FIVE WRITERS — and **read by nothing**: the Enter button below carries no `disabled` and never
// has, in either branch. It could not change an answer, so it was a line no reader could trust; the
// whole chain went with it (the state, its five setters and the prop), which is another seven lines
// off App.tsx's ceiling.
//
// **AND ITS DEADNESS IS EXPLAINED BY SU1's OWN RULING TWO LINES INTO THE BODY**, which is why this
// is a clean removal rather than a repair: *"the member never has to wait at the door ... let them
// walk in now and watch them arrive."* The prop is what is left of a gate that ruling retired, and
// what really reveals the ready panel is `status`, which is untouched.
import { useEffect, useState } from "react";

import { DEFAULT_LANGUAGE } from "../language.js";
import { reportTypeLabel } from "../reportLabels.js";
import { t } from "../strings.js";
import { ModalBackdrop } from "./ModalBackdrop";

export function RoomBuildOverlay({
  status,
  name,
  reports,
  onEnter,
  language = DEFAULT_LANGUAGE,
}: {
  status: "preparing" | "ready";
  name: string;
  reports: Array<{ type: string; title?: string; ok: boolean }>;
  onEnter: () => void;
  language?: string;
}) {
  const firstName = name ? name.split(/\s+/)[0] : "";
  const readyCount = reports.filter((r) => r.ok).length;

  // Live status in the pulsating bar so the wait is informed. Cycles through the real phases while
  // the readings are written (interpretation is the long part). 14-B2: the four are catalog rows.
  const PHASES = ["build.phase1", "build.phase2", "build.phase3", "build.phase4"].map((k) => t(language, k));
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (status !== "preparing") return;
    const timer = setInterval(() => setPhase((p) => (p + 1) % PHASES.length), 3500);
    return () => clearInterval(timer);
  }, [status]);

  return (
    <ModalBackdrop label={t(language, "build.label")} className="room-build">
      <div className="room-build__panel">
        {status === "preparing" ? (
          <>
            <div className="room-build__spinner" aria-hidden="true" />
            <h2>{t(language, "build.title")}</h2>
            <div className="room-build__status is-generating" role="status" aria-live="polite">
              {readyCount >= reports.length && reports.length ? t(language, "build.phase4") : PHASES[phase]}
            </div>
            <p className="room-build__count">{t(language, "build.count", { ready: readyCount, total: reports.length })}</p>
            {reports.length ? (
              <ul className="room-build__list">
                {reports.map((report) => (
                  <li key={report.type} className={report.ok ? "is-ready" : "is-pending"}>
                    <span>{report.ok ? "✓" : "•"}</span>
                    {reportTypeLabel(report.type)}
                    {report.ok ? "" : ` ${t(language, "build.writing")}`}
                  </li>
                ))}
              </ul>
            ) : null}
            {/* SU1 no-hostage: the member never has to wait at the door. Your room already shows
                these three landing live, so let them walk in now and watch them arrive. */}
            <p className="room-build__hint">{t(language, "build.noWait")}</p>
            <button className="button button--primary" type="button" onClick={onEnter}>
              {t(language, "build.enter")}
            </button>
          </>
        ) : (
          <>
            <p className="eyebrow">{t(language, "build.readyEyebrow")}</p>
            <h2>{firstName ? t(language, "build.welcomeNamed", { name: firstName }) : t(language, "build.welcome")}</h2>
            {/* SD1: nothing is generating behind this screen any more, so the list no longer says
                a reading "will finish shortly" (it will not: it is waiting for her). It names what
                is hers to open, and the room's own slots do the rest. */}
            <ul className="room-build__list">
              {reports.map((report) => (
                <li key={report.type} className={report.ok ? "is-ready" : "is-pending"}>
                  <span>{report.ok ? "✓" : "•"}</span>
                  {report.title || reportTypeLabel(report.type)}
                </li>
              ))}
            </ul>
            <p className="room-build__hint">{t(language, "build.readyHint")}</p>
            <button className="button button--primary" type="button" onClick={onEnter}>
              {t(language, "build.enter")}
            </button>
          </>
        )}
      </div>
    </ModalBackdrop>
  );
}
